"use client";

import type React from "react";
import { FormBuilder } from "../../../components/form/FormBuilder";
import { FormSchema } from "../../../interfaces/components/form";
import { FormComponent } from "../../../shared/utils/enums";
import LogoSVG from "../../../assets/images/icons/logo-dark.svg";
import LogoWhiteSVG from "../../../assets/images/icons/logo-white.svg";
import { useNavigate } from "react-router-dom";
import { login as loginAction } from "./actions";
import { useTheme } from "../../../shared/hooks/useTheme";

const Login = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const loginSchema: FormSchema = {
    type: "object",
    properties: {
      email: {
        type: "string",
        label: "Email",
        required: true,
        component: FormComponent.Input,
        componentProps: {
          type: "email",
          placeholder: "Enter your email",
          className:
            "w-full bg-gray-50 dark:bg-darkCardBackground border border-gray-300 dark:border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 dark:text-white",
        },
        validator: [
          {
            regex: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
            message: "Please enter a valid email address",
          },
        ],
      },
      password: {
        type: "string",
        label: "Password",
        required: true,
        component: FormComponent.Input,
        componentProps: {
          type: "password",
          placeholder: "Enter your password",
          className:
            "w-full bg-gray-50 dark:bg-darkCardBackground border border-gray-300 dark:border-gray-700 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 dark:text-white",
        },
        validator: [],
      },
    },
  };

  const handleSubmit = async (values: any) => {
    const result = await loginAction(values);
    if (!!result) {
      navigate("/dashboard");
    }
  };

  return (
    <div className="flex min-h-screen bg-white dark:bg-gray-900">
      {/* Left side with logo and welcome message */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary dark:bg-blue-600 justify-center items-center p-12">
        <div className="max-w-md text-white">
          <img
            src={isDark ? LogoWhiteSVG : LogoSVG}
            alt="Logo"
            className="h-16 w-auto mb-8"
          />
          <h2 className="text-3xl font-bold mb-4">
            Healthcare Management System
          </h2>
          <p className="text-xl opacity-90">
            Securely access your patient records and healthcare data
          </p>
        </div>
      </div>

      {/* Right side with login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo - only shown on smaller screens */}
          <div className="lg:hidden flex justify-center mb-8">
            <img
              src={isDark ? LogoWhiteSVG : LogoSVG}
              alt="Logo"
              className="h-14 w-auto"
            />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Welcome back
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            Please sign in to your account
          </p>

          <FormBuilder
            schema={loginSchema}
            onSubmit={handleSubmit}
            initialValues={{}}
            buttonType={"submit"}
            gridConfig={{ mobile: 1, desktop: 1 }}
            className="space-y-6 dark:text-white"
            submitButtonClassName="!w-full !bg-primary hover:!bg-blue-600 !text-white !py-2.5 !rounded-xl !transition-all !duration-200 !font-medium"
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
